'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft } from 'lucide-react';
import { DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useCreateWebsiteConversionMutation, useCreateCallConversionMutation } from '@/lib/query-options';
import { HubSpotEventsTab } from '@/components/settings/hubspot-events-tab';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

type ConversionTab = 'google' | 'hubspot';
type ChannelType = 'website' | 'call';

interface CreateConversionFormProps {
  /** Called after one or more conversions are created. Receives their registry IDs. */
  onCreated?: (registryIds: number[]) => void;
  /** Called when the cancel button is pressed. */
  onCancel: () => void;
  /** Label for the cancel/back button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Hide the Google Tag / HubSpot source tab bar (used when the parent dialog drives source selection). */
  showSourceTabs?: boolean;
  /** Which source tab to show initially. Defaults to "google". */
  defaultTab?: ConversionTab;
}

export function CreateConversionForm({
  onCreated,
  onCancel,
  cancelLabel = 'Cancel',
  showSourceTabs = true,
  defaultTab = 'google',
}: CreateConversionFormProps) {
  const { symbol } = useCurrencySymbol();
  const [tab, setTab] = useState<ConversionTab>(defaultTab);
  const [channelType, setChannelType] = useState<ChannelType>('website');
  const [hubspotFooter, setHubspotFooter] = useState<{
    primaryLabel: string;
    primaryDisabled: boolean;
    onPrimary: () => void;
    onBack?: () => void;
    leftContent?: React.ReactNode;
  } | null>(null);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | undefined>();
  const [value, setValue] = useState<number | undefined>();
  const [primaryForGoal, setPrimaryForGoal] = useState(true);
  const [funnelStage, setFunnelStage] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const createWebsiteMutation = useCreateWebsiteConversionMutation();
  const createCallMutation = useCreateCallConversionMutation();

  // Tell-on-click validation: Create stays enabled while idle; clicking it marks
  // the missing field instead of sitting silently disabled.
  const handleCreate = async () => {
    if (!name.trim()) {
      setNameError('Required');
      return;
    }

    let created;
    if (channelType === 'website') {
      created = await createWebsiteMutation.mutateAsync({
        name,
        category: 'DEFAULT',
        value,
        always_use_default_value: !!value,
        count_as_conversion: primaryForGoal,
        funnel_stage: funnelStage || undefined,
      });
    } else {
      created = await createCallMutation.mutateAsync({
        name,
        phone_number: phoneNumber || undefined,
        category: 'DEFAULT',
        value,
        always_use_default_value: !!value,
        count_as_conversion: primaryForGoal,
        funnel_stage: funnelStage || undefined,
      });
    }
    if (created.registry_id != null) {
      onCreated?.([created.registry_id]);
    }
  };

  const isPending = createWebsiteMutation.isPending || createCallMutation.isPending;

  return (
    <>
      {/* Source tabs */}
      {showSourceTabs && (
        <div className="flex gap-4 border-b border-gray-200">
          <button
            onClick={() => setTab('google')}
            className={cn(
              'flex items-center gap-2 px-1 pb-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
              tab === 'google'
                ? 'border-[#290BD9] text-[#290BD9]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <img src="/google-ads.svg" alt="" className="h-4 w-4" />
            Google Tag
          </button>
          <button
            onClick={() => setTab('hubspot')}
            className={cn(
              'flex items-center gap-2 px-1 pb-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
              tab === 'hubspot'
                ? 'border-[#290BD9] text-[#290BD9]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <img src="/hubspot.png" alt="" className="h-4 w-4" />
            HubSpot
          </button>
        </div>
      )}

      <div className="min-h-[360px]">
        {tab === 'hubspot' ? (
          <div className="py-4">
            <HubSpotEventsTab onFooterChange={setHubspotFooter} onCompleted={onCreated} />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="cf-name">Name *</Label>
                <FieldError message={nameError} />
              </div>
              <div className="relative">
                <Input
                  id="cf-name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameError(undefined); }}
                  placeholder="e.g., Purchase, Sign-up, Lead"
                  error={!!nameError}
                  className={nameError ? 'pr-8' : ''}
                />
                <InputAdornment error={!!nameError} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cf-stage">Stage</Label>
                <Select value={funnelStage} onValueChange={setFunnelStage}>
                  <SelectTrigger id="cf-stage">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LEAD">Lead</SelectItem>
                    <SelectItem value="MQL">MQL</SelectItem>
                    <SelectItem value="SQL">SQL</SelectItem>
                    <SelectItem value="CUSTOMER">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-value">Conversion Value</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{symbol}</span>
                  <Input
                    id="cf-value"
                    type="number"
                    step="0.01"
                    value={value || ''}
                    onChange={(e) => setValue(e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-channel">Channel</Label>
                <Select value={channelType} onValueChange={(v) => setChannelType(v as ChannelType)}>
                  <SelectTrigger id="cf-channel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-type">Conversion Type</Label>
                <Select
                  value={primaryForGoal ? 'primary' : 'secondary'}
                  onValueChange={(v) => setPrimaryForGoal(v === 'primary')}
                >
                  <SelectTrigger id="cf-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {channelType === 'call' && (
              <div className="space-y-2">
                <Label htmlFor="cf-phone">Phone Number</Label>
                <Input
                  id="cf-phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {tab === 'hubspot' ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hubspotFooter?.onBack && (
              <Button variant="outline" onClick={hubspotFooter.onBack}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            {hubspotFooter?.leftContent}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button
              onClick={hubspotFooter?.onPrimary}
              disabled={!hubspotFooter || hubspotFooter.primaryDisabled}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
            >
              {hubspotFooter?.primaryLabel ?? 'Configure Stages'}
            </Button>
          </div>
        </div>
      ) : (
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isPending}
            className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
          >
            {isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      )}
    </>
  );
}
